import { useCallback, useEffect, useState } from "react";
import { Input, Select } from "../../../ui";
import { formatSar } from "../../../../utils/billingMoney";
import { adminGetAiRolloutReportAPI } from "../../../../utils/api/aiWallet";

// Provider cost per operation is small: four decimals of a dollar are enough to compare.
const usd = (value) => (value === null || value === undefined ? "-" : `$${Number(Number(value).toFixed(4))}`);
const pct = (value) => (value === null || value === undefined ? "-" : `${value}%`);
const sar = (value, lang) => (value === null || value === undefined ? "-" : formatSar(value, lang));
const PERIODS = ["7", "30", "90", ""];

// Internal analysis for deciding when (and with which holds and prices) AI
// charging can be enforced. In shadow mode every AI operation is priced as
// if it were charged; this view shows what that would have meant: cost per
// operation, the hypothetical charge, how often the configured hold or the
// wallet balance would not have been enough, and what LUD absorbs. The hold
// sizing table compares the customer charge with the TOTAL provider spend of
// each operation, which the hold must cover as a provider budget in enforce.
// One pricing version at a time: charges, holds and budgets only compare
// within the terms they were computed with. Never shown to restaurants.
const AiWalletAdminRollout = ({ text, lang }) => {
  const t = text.admin;
  const r = t.rollout;
  const [mode, setMode] = useState("shadow");
  const [days, setDays] = useState("30");
  const [storeId, setStoreId] = useState("");
  // "" = the server's default (the active version, else the newest with data).
  const [pricingVersion, setPricingVersion] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const from = days ? new Date(Date.now() - Number(days) * 24 * 60 * 60 * 1000).toISOString() : "";
      setData(await adminGetAiRolloutReportAPI({ mode, storeId, from, pricingVersion }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [mode, days, storeId, pricingVersion]);
  useEffect(() => {
    load();
  }, [load]);

  const label = (key) => text.features[key] || t.holdLabels[key] || key;

  return (
    <div className="aiw-admin">
      <p className="aiw-muted">{r.note}</p>
      <div className="aiw-toolbar">
        <Select label={t.colMode} value={mode} onChange={(event) => { setMode(event.target.value); setPricingVersion(""); }} options={[{ value: "shadow", label: t.modes.shadow }, { value: "enforce", label: t.modes.enforce }]} />
        <Select label={r.period} value={days} onChange={(event) => setDays(event.target.value)} options={PERIODS.map((value) => ({ value, label: r.periods[value || "all"] }))} />
        <Input label={t.storeIdPlaceholder} value={storeId} onChange={(event) => setStoreId(event.target.value.trim())} />
        {data?.pricingVersions && (
          <Select
            label={r.pricingVersion}
            value={pricingVersion === "" ? String(data.pricingVersion ?? "") : pricingVersion}
            onChange={(event) => setPricingVersion(event.target.value)}
            options={[...data.pricingVersions.map((v) => ({ value: String(v.version), label: r.versionOption(v) })), ...(data.pricingVersions.length > 1 ? [{ value: "all", label: r.allVersions }] : []), ...(data.pricingVersions.length ? [] : [{ value: "", label: r.noVersions }])]}
          />
        )}
      </div>
      {error && <div className="billing-error">{error}</div>}
      {loading && !data && <div className="billing-loading">{text.loading}</div>}
      {data && (
        <>
          <div className="aiw-kpis">
            <div className="aiw-kpi">
              <span>{r.currentMode}</span>
              <strong>{t.modes[data.currentMode] || data.currentMode}</strong>
            </div>
            <div className="aiw-kpi">
              <span>{r.operations}</span>
              <strong>{data.operations}</strong>
            </div>
            <div className="aiw-kpi">
              <span>{r.openCharges}</span>
              <strong>{data.openCharges}</strong>
            </div>
            {mode === "enforce" && (
              <div className="aiw-kpi">
                <span>{r.refused}</span>
                <strong>{data.refusedOperations ?? 0}</strong>
              </div>
            )}
          </div>
          {data.truncated && <p className="aiw-muted">{r.truncated}</p>}
          {!data.operations && <div className="billing-empty-note">{r.empty}</div>}
          {data.byFeature.map((row) => (
            <section key={row.key} className="aiw-panel" aria-label={label(row.key)}>
              <h4>{label(row.key)}</h4>
              <div className="aiw-kpis">
                <div className="aiw-kpi">
                  <span>{r.operations}</span>
                  <strong>{row.operations}</strong>
                  <small className="aiw-muted">
                    {r.success} {pct(row.successPct)} · {r.billable} {row.billableOperations}
                  </small>
                </div>
                <div className="aiw-kpi">
                  <span>{r.avgCost}</span>
                  <strong dir="ltr">{usd(row.providerCostUsd.avg)}</strong>
                  <small className="aiw-muted" dir="ltr">
                    p50 {usd(row.providerCostUsd.p50)} · p90 {usd(row.providerCostUsd.p90)}
                  </small>
                </div>
                <div className="aiw-kpi">
                  <span>{r.tailCost}</span>
                  <strong dir="ltr">{usd(row.providerCostUsd.p95)}</strong>
                  <small className="aiw-muted" dir="ltr">
                    p99 {usd(row.providerCostUsd.p99)} · max {usd(row.providerCostUsd.max)}
                  </small>
                </div>
                <div className="aiw-kpi">
                  <span>{r.avgCharge}</span>
                  <strong>{sar(row.customerChargeMinor.avg, lang)}</strong>
                  <small className="aiw-muted">
                    p95 {sar(row.customerChargeMinor.p95, lang)} · max {sar(row.customerChargeMinor.max, lang)}
                  </small>
                </div>
                <div className="aiw-kpi">
                  <span>{r.exceedingHold}</span>
                  <strong>{pct(row.exceedingHoldPct)}</strong>
                  <small className="aiw-muted">
                    {r.hold} {row.configuredHoldMinor.map((value) => formatSar(value, lang)).join(" / ")} · {r.maxOver} {formatSar(row.maxOvershootMinor, lang)} · {r.unusedHold} {sar(row.avgUnusedHoldMinor, lang)}
                  </small>
                </div>
                <div className="aiw-kpi">
                  <span>{r.insufficientWallet}</span>
                  <strong>{pct(row.insufficientWalletPct)}</strong>
                  <small className="aiw-muted">{r.insufficientNote}</small>
                </div>
                <div className="aiw-kpi">
                  <span>{r.absorbed}</span>
                  <strong dir="ltr">{usd(row.avgAbsorbedProviderCostUsd)}</strong>
                  <small className="aiw-muted" dir="ltr">
                    {r.failedCost} {usd(row.failedOperationProviderCostUsd)}
                  </small>
                </div>
                <div className="aiw-kpi">
                  <span>{r.margin}</span>
                  <strong>{formatSar(row.totals.marginMinor, lang)}</strong>
                  <small className="aiw-muted">
                    {t.summaryProviderCost} {formatSar(row.totals.providerCostMinor, lang)} · {r.charged} {formatSar(row.totals.customerChargeMinor, lang)}
                  </small>
                </div>
                <div className="aiw-kpi">
                  <span>{r.reliability}</span>
                  <strong>{pct(row.fallbackUsedPct)}</strong>
                  <small className="aiw-muted">
                    {r.retries} {pct(row.operationsWithRetriesPct)} · {r.requests} {row.avgRequestsPerOperation ?? "-"}
                  </small>
                </div>
              </div>
              {row.models.length > 0 && (
                <p className="aiw-muted" dir="ltr">
                  {row.models.map((model) => `${model.model} (${model.operations})`).join(" · ")}
                </p>
              )}
            </section>
          ))}
          {data.byOperationType.length > 0 && (
            <section className="aiw-panel" aria-label={r.holdSizing}>
              <h4>{r.holdSizing}</h4>
              <p className="aiw-muted">{r.holdSizingNote}</p>
              {data.activePricingVersion !== null && data.activePricingVersion !== undefined && <p className="aiw-muted">{r.activeVersionNote(data.activePricingVersion)}</p>}
              <div className="aiw-table-wrap">
                <table className="aiw-table">
                  <thead>
                    <tr>
                      <th>{r.operationType}</th>
                      <th>{r.operations}</th>
                      <th>{r.customerCharge}</th>
                      <th>{r.providerSpend}</th>
                      <th>{r.hold}</th>
                      <th>{r.providerBudget}</th>
                      <th>{r.overBudget}</th>
                      <th>{r.overHold}</th>
                      <th>{r.suggestedHold}</th>
                      <th>{r.insufficientWallet}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byOperationType.map((row) => {
                      const suggestion = row.suggestedHold;
                      return (
                        <tr key={row.key}>
                          <td>{label(row.key)}</td>
                          <td>{row.operations}</td>
                          <td>
                            {r.avg} {sar(row.customerChargeMinor.avg, lang)}
                            <small className="aiw-muted"> · p95 {sar(row.customerChargeMinor.p95, lang)} · p99 {sar(row.customerChargeMinor.p99, lang)} · max {sar(row.customerChargeMinor.max, lang)}</small>
                          </td>
                          <td dir="ltr">
                            {r.avg} {usd(row.providerCostUsd.avg)}
                            <small className="aiw-muted"> · p95 {usd(row.providerCostUsd.p95)} · p99 {usd(row.providerCostUsd.p99)} · max {usd(row.providerCostUsd.max)}</small>
                            {row.unknownCostOperations > 0 && <small className="aiw-muted"> · {r.unknownCost(row.unknownCostOperations)}</small>}
                          </td>
                          <td>{row.configuredHoldMinor.map((value) => formatSar(value, lang)).join(" / ")}</td>
                          <td dir="ltr">{(row.providerBudgetUsd || []).map(usd).join(" / ") || "-"}</td>
                          <td>
                            <strong>{pct(row.exceedingBudgetPct)}</strong> <small className="aiw-muted">({row.exceedingBudgetCount ?? 0})</small>
                          </td>
                          <td>
                            {pct(row.exceedingHoldPct)} <small className="aiw-muted">({row.exceedingHoldCount ?? 0})</small>
                          </td>
                          <td>
                            {!suggestion ? (
                              "-"
                            ) : suggestion.reliable ? (
                              <>
                                <strong>{formatSar(suggestion.minor, lang)}</strong>
                                <small className="aiw-muted" dir="ltr">
                                  {" "}
                                  {r.suggestedNote(suggestion)}
                                </small>
                              </>
                            ) : suggestion.reason === "INSUFFICIENT_DATA" ? (
                              <>
                                {r.notEnoughData(suggestion)}
                                {suggestion.indicativeMinor !== null && (
                                  <small className="aiw-muted">
                                    {" "}
                                    · {formatSar(suggestion.indicativeMinor, lang)} ({r.indicative})
                                  </small>
                                )}
                              </>
                            ) : suggestion.reason === "NO_PRICING" ? (
                              r.noPricingData
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>{pct(row.insufficientWalletPct)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default AiWalletAdminRollout;
